import { createStore } from 'vuex';

import alert, { AlertState } from './store/alert.store';
import appSection, { AppSectionState } from './store/app-section.store';
import auth, { AuthState } from './store/auth.store';
import { CageState, default as cage } from './store/cage.store';
import commission, { CommissionState } from './store/commission.store';
import changelog, { ChangelogState } from './store/changelog.store';
import comment, { CommentState } from './store/comment.store';
import control, { ControlState } from './store/control.store';
import dataExport, { DataExportState } from './store/data-export.store';
import deposit, { DepositState } from './store/deposit.store';
import itemStatement, { ItemStatementState } from './store/item-statement.store';
import report, { ReportsState } from './store/report.store';
import grade, { GradesState } from './store/grade.store';
import gradeV2, { GradeV2State } from './store/grade-v2.store';
import buyback, { BuybackState } from './store/buyback.store';
import invoice, { InvoiceState } from './store/invoice.store';
import paymentLink, { PaymentLinkState } from './store/payment-link.store';
import accountingMapping, { AccountingMappingState } from './store/accounting-mapping.store';
import taxAccountingEntry, { TaxAccountingEntryState } from './store/tax-accounting-entry.store';
import marketplacePaymentImport, { MarketplacePaymentImportState } from './store/marketplace-payment-import.store';
import customer, { CustomerState } from './store/customer.store';
import crmTask, { CrmTaskState } from './store/crm-task.store';
import entryFlowStats, { EntryFlowStatsState } from './store/entry-flow-stats.store';
import device, { DeviceState } from './store/device.store';
import error, { ErrorState } from './store/error.store';
import statement, { StatementState } from './store/statement.store';
import file, { FileState } from './store/file.store';
import focus, { FocusState } from './store/focus.store';
import globalSettings, { GlobalSettingsState } from './store/global-settings.store';
import location, { LocationState } from './store/location.store';
import marketplaceSku, { MarketplaceSkuState } from './store/marketplace-sku.store';
import matchError, { MatchErrorState } from './store/match-error.store';
import match, { MatchState } from './store/match.store';
import modal, { ModalState } from './store/modal.store';
import orderLine, { OrderLineState } from './store/order-line.store';
import orderReturn, { OrderReturnState } from './store/order-return.store';
import order, { OrderState } from './store/order.store';
import printer, { PrinterState } from './store/printer.store';
import processStep, { ProcessStepState } from './store/process-steps.store';
import processTask, { ProcessTaskState } from './store/process-tasks.store';
import productionOrder, { ProductionOrderState } from './store/production-order.store';
import purchase, { PurchaseState } from './store/purchase.store';
import purchaseItems, { PurchaseItemsState } from './store/purchase-items.store';
import reference, { ReferenceState } from './store/reference.store';
import retrieval, { RetrievalState } from './store/retrieval.store';
import skuDifferenceCost, { SkuDifferenceCostState } from './store/sku-difference-cost.store';
import repairCost, { RepairCostState } from './store/repair-cost.store';
import stock, { StockState } from './store/stock.store';
import supplier, { SupplierState } from './store/supplier.store';
import picking, { PickingState } from './store/picking.store';
import table, { TableState } from './store/table.store';
import marketplace, { MarketplaceState } from './store/marketplace.store';
import user, { UserState } from './store/user.store';
import part, { PartState } from './store/part.store';
import orderSparePart, { OrderSparePartState } from './store/order-spare-part.store';
import runnersQuest, { RunnersQuestState } from './store/runners-quest.store';
import task, { TaskState } from './store/task.store';
import attachments, { AttachmentsState } from './store/file-storage-attachments.store';
import partFailures from './store/part-failure.store';
import ukTransfer, { UkTransferState } from './store/uk-transfer.store';
import supplierGrade, { SupplierGradeState } from './store/supplier-grade.store';
import rmaElement, { RmaElementsState } from './store/rma-element.store';
import unpacking, { UnpackingState } from './store/unpacking.store';
import supplierContact, { SupplierContactState } from './store/supplier-contact.store';
import timer, { TimerState } from './store/timer.store';
import userTimer, { UserTimerState } from './store/user-timer.store';
import chart, { ChartsState } from './store/chart.store';

export interface RootState {
  'app-section': AppSectionState;
  'data-export': DataExportState;
  'global-settings': GlobalSettingsState;
  'marketplace-sku': MarketplaceSkuState;
  'match-error': MatchErrorState;
  'order-line': OrderLineState;
  'order-return': OrderReturnState;
  'process-step': ProcessStepState;
  'process-task': ProcessTaskState;
  'production-order': ProductionOrderState;
  'sku-difference-cost': SkuDifferenceCostState;
  'repair-cost': RepairCostState;
  buyback: BuybackState;
  invoice: InvoiceState;
  'payment-link': PaymentLinkState;
  'accounting-mapping': AccountingMappingState;
  'tax-accounting-entry': TaxAccountingEntryState;
  'marketplace-payment-import': MarketplacePaymentImportState;
  customer: CustomerState;
  'crm-task': CrmTaskState;
  'entry-flow-stats': EntryFlowStatsState;
  timer: TimerState;
  part: PartState;
  'order-spare-part': OrderSparePartState;
  'runners-quest': RunnersQuestState;
  task: TaskState;
  alert: AlertState;
  attachments: AttachmentsState;
  auth: AuthState;
  cage: CageState;
  changelog: ChangelogState;
  chart: ChartsState;
  comment: CommentState;
  commission: CommissionState;
  control: ControlState;
  deposit: DepositState;
  device: DeviceState;
  itemStatement: ItemStatementState;
  error: ErrorState;
  statements: StatementState;
  file: FileState;
  focus: FocusState;
  grade: GradesState;
  gradeV2: GradeV2State;
  location: LocationState;
  marketplace: MarketplaceState;
  match: MatchState;
  modal: ModalState;
  order: OrderState;
  picking: PickingState;
  printer: PrinterState;
  purchase: PurchaseState;
  purchaseItems: PurchaseItemsState;
  reference: ReferenceState;
  report: ReportsState;
  retrieval: RetrievalState;
  stock: StockState;
  supplier: SupplierState;
  supplierGrade: SupplierGradeState;
  supplierContact: SupplierContactState;
  table: TableState<any>;
  unpacking: UnpackingState;
  ukTransfer: UkTransferState;
  user: UserState;
  userTimer: UserTimerState;
  rmaElement: RmaElementsState;
}

export default createStore({
  modules: {
    'app-section': appSection,
    'data-export': dataExport,
    'global-settings': globalSettings,
    'marketplace-sku': marketplaceSku,
    'match-error': matchError,
    'order-line': orderLine,
    'order-return': orderReturn,
    'process-step': processStep,
    'process-task': processTask,
    'production-order': productionOrder,
    'supplier-grade': supplierGrade,
    'sku-difference-cost': skuDifferenceCost,
    'repair-cost': repairCost,
    buyback,
    invoice,
    'payment-link': paymentLink,
    'accounting-mapping': accountingMapping,
    'tax-accounting-entry': taxAccountingEntry,
    'marketplace-payment-import': marketplacePaymentImport,
    customer,
    'crm-task': crmTask,
    'entry-flow-stats': entryFlowStats,
    alert,
    attachments,
    auth,
    supplierContact,
    cage,
    part,
    'order-spare-part': orderSparePart,
    'runners-quest': runnersQuest,
    task,
    partFailures,
    changelog,
    chart,
    comment,
    commission,
    control,
    deposit,
    device,
    itemStatement,
    error,
    statement,
    file,
    focus,
    grade,
    gradeV2,
    location,
    marketplace,
    match,
    modal,
    order,
    picking,
    printer,
    purchase,
    purchaseItems,
    reference,
    report,
    retrieval,
    stock,
    supplier,
    table,
    timer,
    unpacking,
    ukTransfer,
    user,
    userTimer,
    rmaElement
  },
  strict: process.env.NODE_ENV !== 'production'
});
